import React, { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { Info } from 'lucide-react';

const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const colorOf = (n) => (n === 0 ? 'green' : RED.has(n) ? 'red' : 'black');
const CHIPS = [5, 10, 25, 50, 100]; // max stake €100
const PAYOUT = { red: 2, black: 2, green: 14 };

export default function LiveTable({ game }) {
  const Icon = Icons[game.icon] || Icons.Disc3;
  const [stake, setStake] = useState(10);
  const [betColor, setBetColor] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [display, setDisplay] = useState(0);
  const [msg, setMsg] = useState('Place a chip, pick a colour, then spin. Max stake €100.');
  const [history, setHistory] = useState([17, 0, 32, 5, 23]);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const spin = () => {
    if (spinning || !betColor) return;
    setSpinning(true);
    setResult(null);
    setMsg('The wheel is spinning...');
    let ticks = 0;
    timer.current = setInterval(() => {
      setDisplay(Math.floor(Math.random() * 37));
      ticks++;
      if (ticks > 16) {
        clearInterval(timer.current);
        const n = Math.floor(Math.random() * 37);
        setDisplay(n);
        setResult(n);
        const c = colorOf(n);
        const won = c === betColor;
        setMsg(won ? `${n} ${c.toUpperCase()} — demo win €${(stake * PAYOUT[betColor]).toFixed(2)}!` : `${n} ${c.toUpperCase()} — no win this round. (Demo)`);
        setHistory((h) => [n, ...h].slice(0, 8));
        setSpinning(false);
      }
    }, 80);
  };

  const dispColor = colorOf(display);
  const swatch = { red: '#D90035', black: '#151119', green: '#2FBF71' };

  return (
    <div className="xc-fade-up">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: game.grad }}><Icon className="h-4 w-4 text-white" /></span>
          <span className="font-display font-700 text-white">{game.title}</span>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E0B85A]/10 border border-[#E0B85A]/30 text-[#E0B85A]">Live Casino Demo · Max €100</span>
      </div>

      <div className="grid md:grid-cols-[1fr_260px] gap-5 items-start">
        {/* Wheel result */}
        <div className="relative rounded-2xl p-6 overflow-hidden border border-[#E0B85A]/20 grid place-items-center h-64" style={{ background: 'linear-gradient(160deg,#151119,#0D0B10)' }}>
          <div className="absolute inset-0 xc-grid opacity-15" />
          <div className="relative flex flex-col items-center">
            <div className="h-28 w-28 rounded-full grid place-items-center border-4 transition-colors" style={{ borderColor: swatch[dispColor], background: `${swatch[dispColor]}22`, boxShadow: `0 0 40px -6px ${swatch[dispColor]}` }}>
              <span className={`font-display font-700 text-5xl text-white ${spinning ? 'blur-[1px]' : ''}`}>{display}</span>
            </div>
            <div className="mt-3 text-xs uppercase tracking-[0.2em]" style={{ color: swatch[dispColor] === '#151119' ? '#cfcfd6' : swatch[dispColor] }}>{dispColor}</div>
            <div className="mt-2 text-sm text-white/60 text-center max-w-xs">{msg}</div>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <div className="rounded-lg px-3 py-2.5 bg-white/[0.04] border border-white/10 flex items-center justify-between">
            <span className="text-[10px] text-white/40 uppercase">Balance</span><span className="text-white font-600 text-sm">€183.40</span>
          </div>
          <div className="rounded-lg p-3 bg-white/[0.04] border border-white/10">
            <div className="flex items-center justify-between mb-2"><span className="text-[10px] text-white/40 uppercase">Stake</span><span className="text-white font-600 text-sm">€{stake.toFixed(2)}</span></div>
            <div className="grid grid-cols-5 gap-1">
              {CHIPS.map((c) => <button key={c} disabled={spinning} onClick={() => setStake(c)} className={`rounded-md py-1.5 text-xs font-600 border ${stake === c ? 'border-[#E0B85A]/60 text-[#E0B85A] bg-[#E0B85A]/10' : 'border-white/10 text-white/50'} disabled:opacity-40`}>{c}</button>)}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {['red', 'black', 'green'].map((c) => (
              <button key={c} disabled={spinning} onClick={() => setBetColor(c)}
                className={`rounded-lg py-2.5 text-xs font-600 uppercase tracking-wide border-2 disabled:opacity-40 ${betColor === c ? '' : 'opacity-70'}`}
                style={{ borderColor: swatch[c], background: betColor === c ? `${swatch[c]}33` : 'transparent', color: c === 'black' ? '#cfcfd6' : swatch[c] }}>
                {c} {PAYOUT[c]}x
              </button>
            ))}
          </div>
          <button onClick={spin} disabled={spinning || !betColor} className="btn-neon rounded-xl w-full py-4 font-700 tracking-widest disabled:opacity-50 disabled:cursor-not-allowed">
            {spinning ? 'SPINNING...' : !betColor ? 'PICK A COLOUR' : 'SPIN'}
          </button>
          <p className="text-[11px] text-white/40 leading-relaxed flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Demo table — no real money. Stakes are capped at €100 in the Old Quarter.</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-white/40">Recent</span>
        {history.map((n, i) => (
          <span key={i} className="text-xs font-600 h-7 w-7 grid place-items-center rounded-md text-white" style={{ background: `${swatch[colorOf(n)]}${colorOf(n) === 'black' ? '' : '33'}`, border: `1px solid ${swatch[colorOf(n)]}` }}>{n}</span>
        ))}
      </div>
    </div>
  );
}
